import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface ProfileWithUsername {
  username: string | null;
  auth_provider: string | null;
}

export function useUsernameOnboarding() {
  const { user, isLoading: authLoading } = useAuth();
  const [needsUsername, setNeedsUsername] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [authProvider, setAuthProvider] = useState<string | null>(null);

  const checkUsername = useCallback(async () => {
    if (!user) {
      setNeedsUsername(false);
      setIsChecking(false);
      return;
    }

    setIsChecking(true);
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username, auth_provider')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking username:', error);
        setNeedsUsername(false);
      } else if (data) {
        const profile = data as ProfileWithUsername;
        setNeedsUsername(!profile.username);
        setAuthProvider(profile.auth_provider);
      } else {
        // No profile found - should not happen but handle gracefully
        setNeedsUsername(true);
      }
    } catch (err) {
      console.error('Error in username check:', err);
      setNeedsUsername(false);
    } finally {
      setIsChecking(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) {
      checkUsername();
    }
  }, [authLoading, checkUsername]);

  const completeOnboarding = useCallback(() => {
    setNeedsUsername(false);
  }, []);

  return {
    needsUsername,
    isChecking: isChecking || authLoading,
    authProvider,
    completeOnboarding,
    recheckUsername: checkUsername,
  };
}
